import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

interface ImageLightboxProps {
  open: boolean;
  src: string;
  alt: string;
  title?: string;
  /** Optional actions rendered in the top bar (e.g. a download button). */
  actions?: ReactNode;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

/**
 * Full-screen zoom viewer for a single image. Clicking the image toggles
 * between "fit to screen" and a zoomed-in view; drag pans while zoomed.
 */
export default function ImageLightbox({
  open,
  src,
  alt,
  title,
  actions,
  onClose,
}: ImageLightboxProps) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // reset zoom + pan whenever (re)opened
  useEffect(() => {
    if (open) {
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open, src]);

  // scroll lock + focus management + keyboard handling
  useEffect(() => {
    if (!open) return undefined;

    restoreRef.current = (document.activeElement as HTMLElement) ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      const node = dialogRef.current;
      if (!node) return;
      const items = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (items.length > 0) items[0].focus();
      else node.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(focusTimer);
      restoreRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const clamp = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

  const applyZoom = (next: number) => {
    const clamped = clamp(next);
    setZoom(clamped);
    if (clamped === 1) setOffset({ x: 0, y: 0 });
  };

  const toggleZoom = () => applyZoom(zoom > 1 ? 1 : 2);

  const onPointerDown = (event: React.PointerEvent<HTMLImageElement>) => {
    if (zoom <= 1) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      x: offset.x,
      y: offset.y,
      startX: event.clientX,
      startY: event.clientY,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLImageElement>) => {
    if (!dragRef.current) return;
    const { x, y, startX, startY } = dragRef.current;
    setOffset({ x: x + (event.clientX - startX), y: y + (event.clientY - startY) });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const zoomed = zoom > 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-foreground-950/85 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={title || alt}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="flex h-full w-full flex-col outline-none"
      >
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 md:px-6">
          <div className="min-w-0">
            {title ? (
              <h3 className="truncate text-sm font-semibold text-background-50">{title}</h3>
            ) : null}
            <p className="truncate text-xs text-background-200">
              {zoomed ? t('common.imageViewer.zoomedHint') : t('common.imageViewer.hint')}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="flex h-9 w-9 items-center justify-center rounded-md text-background-100 transition-colors hover:bg-background-50/10 cursor-pointer"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div
          className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden p-4 md:p-8"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <img
            src={src}
            alt={alt}
            title={alt}
            onClick={toggleZoom}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            draggable={false}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})` }}
            className={cn(
              'max-h-full max-w-full select-none rounded-md object-contain shadow-none transition-transform duration-150 ease-smooth',
              zoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
            )}
          />
        </div>

        <div className="flex shrink-0 items-center justify-center gap-1.5 px-4 pb-5">
          <button
            type="button"
            onClick={() => applyZoom(zoom - ZOOM_STEP)}
            disabled={zoom <= MIN_ZOOM}
            aria-label={t('common.imageViewer.zoomOut')}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/10 text-background-50 transition-colors hover:bg-background-50/20 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            <i className="ri-zoom-out-line text-lg" aria-hidden="true" />
          </button>
          <span className="min-w-[3.5rem] text-center text-xs font-medium text-background-100 tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => applyZoom(zoom + ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            aria-label={t('common.imageViewer.zoomIn')}
            className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/10 text-background-50 transition-colors hover:bg-background-50/20 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            <i className="ri-zoom-in-line text-lg" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => applyZoom(1)}
            className="ms-1 inline-flex h-9 items-center gap-1.5 rounded-md bg-background-50/10 px-3 text-xs font-medium text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
          >
            <i className="ri-aspect-ratio-line text-base" aria-hidden="true" />
            {t('common.imageViewer.reset')}
          </button>
        </div>
      </div>
    </div>
  );
}